// Original source - https://github.com/facebookarchive/flux/blob/main/src/Dispatcher.js
/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree. An additional grant
 * of patent rights can be found in the PATENTS file in the same directory.
 *
 * @providesModule Dispatcher
 * @flow
 * @preventMunge
 */

/*
 * Changes made by Eric Grimard - 2025-02-21
 * Removed redundant `export type DispatchToken = string;` and changed the type to string.
 * Converted single Apostrophe (') to Quotation mark (")
 * Converted 'var' to 'const' and 'let'
 * Added public / private accessibility to methods and fields.
 * Converted For loop using index to for-of loop
 */

"use strict";

import invariant from "invariant";

const prefix = "ID_";

/**
 * Dispatcher is used to broadcast payloads to registered callbacks. This is
 * different from generic pub-sub systems in two ways:
 *
 *   1) Callbacks are not subscribed to particular events. Every payload is
 *      dispatched to every registered callback.
 *   2) Callbacks can be deferred in whole or part until other callbacks have
 *      been executed.
 *
 * For example, consider this hypothetical flight destination form, which
 * selects a default city when a country is selected:
 *
 *   var flightDispatcher = new Dispatcher();
 *
 *   // Keeps track of which country is selected
 *   var CountryStore = {country: null};
 *
 *   // Keeps track of which city is selected
 *   var CityStore = {city: null};
 *
 *   // Keeps track of the base flight price of the selected city
 *   var FlightPriceStore = {price: null}
 *
 * When a user changes the selected city, we dispatch the payload:
 *
 *   flightDispatcher.dispatch({
 *     actionType: 'city-update',
 *     selectedCity: 'paris'
 *   });
 *
 * This payload is digested by `CityStore`:
 *
 *   flightDispatcher.register(function(payload) {
 *     if (payload.actionType === 'city-update') {
 *       CityStore.city = payload.selectedCity;
 *     }
 *   });
 *
 * When the user selects a country, we dispatch the payload:
 *
 *   flightDispatcher.dispatch({
 *     actionType: 'country-update',
 *     selectedCountry: 'australia'
 *   });
 *
 * This payload is digested by both stores:
 *
 *   CountryStore.dispatchToken = flightDispatcher.register(function(payload) {
 *     if (payload.actionType === 'country-update') {
 *       CountryStore.country = payload.selectedCountry;
 *     }
 *   });
 *
 * When the callback to update `CountryStore` is registered, we save a reference
 * to the returned token. Using this token with `waitFor()`, we can guarantee
 * that `CountryStore` is updated before the callback that updates `CityStore`
 * needs to query its data.
 *
 *   CityStore.dispatchToken = flightDispatcher.register(function(payload) {
 *     if (payload.actionType === 'country-update') {
 *       // `CountryStore.country` may not be updated.
 *       flightDispatcher.waitFor([CountryStore.dispatchToken]);
 *       // `CountryStore.country` is now guaranteed to be updated.
 *
 *       // Select the default city for the new country
 *       CityStore.city = getDefaultCityForCountry(CountryStore.country);
 *     }
 *   });
 *
 * The usage of `waitFor()` can be chained, for example:
 *
 *   FlightPriceStore.dispatchToken =
 *     flightDispatcher.register(function(payload) {
 *       switch (payload.actionType) {
 *         case 'country-update':
 *         case 'city-update':
 *           flightDispatcher.waitFor([CityStore.dispatchToken]);
 *           FlightPriceStore.price =
 *             getFlightPriceStore(CountryStore.country, CityStore.city);
 *           break;
 *     }
 *   });
 *
 * The `country-update` payload will be guaranteed to invoke the stores'
 * registered callbacks in order: `CountryStore`, `CityStore`, then
 * `FlightPriceStore`.
 */
class Dispatcher<TPayload> {
    private callbacks: { [key: string]: (payload: TPayload) => void };
    private isDispatchingAction: boolean;
    private isHandled: { [key: string]: boolean };
    private isPending: { [key: string]: boolean };
    private lastID: number;
    private pendingPayload: TPayload;

    constructor() {
        this.callbacks = {};
        this.isDispatchingAction = false;
        this.isHandled = {};
        this.isPending = {};
        this.lastID = 1;
    }

    /**
   * Registers a callback to be invoked with every dispatched payload. Returns
   * a token that can be used with `waitFor()`.
   */
    public register(callback: (payload: TPayload) => void): string {
        const id = prefix + this.lastID++;
        this.callbacks[id] = callback;
        return id;
    }

    /**
   * Removes a callback based on its token.
   */
    public unregister(id: string): void {
        invariant(
            this.callbacks[id],
            "Dispatcher.unregister(...): `%s` does not map to a registered callback.",
            id
        );
        delete this.callbacks[id];
    }

    /**
   * Waits for the callbacks specified to be invoked before continuing execution
   * of the current callback. This method should only be used by a callback in
   * response to a dispatched payload.
   */
    public waitFor(ids: Array<string>): void {
        invariant(
            this.isDispatchingAction,
            "Dispatcher.waitFor(...): Must be invoked while dispatching."
        );

        for (let id of ids) {
            if (this.isPending[id]) {
                invariant(
                    this.isHandled[id],
                    "Dispatcher.waitFor(...): Circular dependency detected while " + "waiting for `%s`.",
                    id
                );
                continue;
            }
            invariant(
                this.callbacks[id],
                "Dispatcher.waitFor(...): `%s` does not map to a registered callback.",
                id
            );
            this.invokeCallback(id);
        }
    }

    /**
   * Dispatches a payload to all registered callbacks.
   */
    public dispatch(payload: TPayload): void {
        invariant(
            !this.isDispatchingAction,
            "Dispatch.dispatch(...): Cannot dispatch in the middle of a dispatch."
        );
        this.startDispatching(payload);
        try {
            for (let id in this.callbacks) {
                if (this.isPending[id]) {
                    continue;
                }
                this.invokeCallback(id);
            }
        }
        finally {
            this.stopDispatching();
        }
    }

    /**
   * Is this Dispatcher currently dispatching.
   */
    public isDispatching(): boolean {
        return this.isDispatchingAction;
    }

    /**
   * Call the callback stored with the given id. Also do some internal
   * bookkeeping.
   *
   * @internal
   */
    private invokeCallback(id: string): void {
        this.isPending[id] = true;
        this.callbacks[id](this.pendingPayload);
        this.isHandled[id] = true;
    }

    /**
   * Set up bookkeeping needed when dispatching.
   *
   * @internal
   */
    private startDispatching(payload: TPayload): void {
        for (let id in this.callbacks) {
            this.isPending[id] = false;
            this.isHandled[id] = false;
        }
        this.pendingPayload = payload;
        this.isDispatchingAction = true;
    }

    /**
   * Clear bookkeeping used for dispatching.
   *
   * @internal
   */
    private stopDispatching(): void {
        delete this.pendingPayload;
        this.isDispatchingAction = false;
    }
}

export { Dispatcher };
